import { useRef, type CSSProperties } from "react";
import { bringIntoView } from "../../ui/scroll";
import { AppIcon } from "../AppIcon";
import type { Channel, Menu, Product, Promotions } from "../../api/types";
import { priceOf } from "../../features/promotions/promotions";
import { categoryColor, productsIn, sellableCategories } from "../../features/sale/menu-view";
import { isDrink } from "../../features/sale/quick-entry";
import { t } from "../../i18n";
import { eurosToCents, formatEuro } from "../../lib/money";

/** A row of the product list: a product, or a drink in one of its sizes (prototype obExpand). */
export interface PickEntry {
  product: Product;
  /** A drink with several sizes has a row per size, tapped in one go. */
  variant: string | undefined;
}

/** The products of a category, or what matches the search (name or code), drinks per size. */
export function pickEntries(menu: Menu, category: string | null, query: string): PickEntry[] {
  const typed = query.trim().toLowerCase();
  const products =
    typed === ""
      ? category === null
        ? []
        : productsIn(menu, category)
      : sellableCategories(menu)
          .flatMap((item) => productsIn(menu, item.id))
          .filter(
            (product) =>
              product.name.toLowerCase().includes(typed) || (product.code ?? "").toLowerCase().startsWith(typed),
          );
  return products.flatMap((product): PickEntry[] =>
    isDrink(menu, product) && product.variants.length > 1
      ? product.variants.map((variant) => ({ product, variant: variant.label }))
      : [{ product, variant: undefined }],
  );
}

/** "€ 2,75" for a size or a single price, "€ 11,90+" for a product in sizes (prototype obPrice). */
function priceLabel(promotions: Promotions | null, channel: Channel, entry: PickEntry): string {
  const { product, variant } = entry;
  const price = (label: string) => eurosToCents(priceOf(promotions, channel, product, label));
  if (variant !== undefined) return formatEuro(price(variant));
  if (product.variants.length === 1) return formatEuro(price(product.variants[0]!.label));
  return `${formatEuro(Math.min(...product.variants.map((item) => price(item.label))))}+`;
}

interface Props {
  menu: Menu;
  channel: Channel;
  promotions: Promotions | null;
  soldOut: ReadonlySet<string>;
  /** The category shown; null: the first category. */
  category: string | null;
  query: string;
  /** iPad: the categories as chips that wrap and the products as tiles (prototype obPickIpad). */
  wide: boolean;
  /** The price on the tiles of a phone too: a guest at the table chooses with the price in view (QR-bestellen). */
  prices?: boolean;
  onCategory: (category: string) => void;
  onQuery: (query: string) => void;
  /** Left out on the guest's page at the table (QR-bestellen), where there is nothing to go back to. */
  onBack?: () => void;
  onProduct: (entry: PickEntry) => void;
  onAllergens: () => void;
}

/**
 * Choosing a product: search by name or code, ⚠ Allergenen, sold out greyed out. A phone shows the categories
 * and the products as the till on the owner's phone does (Furkan, 18/10/2026; before: first the category tiles,
 * then a list with prices, prototype obPickHtml); an iPad the chips and tiles of prototype obPickIpad.
 */
export function PickView({
  menu,
  channel,
  promotions,
  soldOut,
  category,
  query,
  wide,
  prices = false,
  onCategory,
  onQuery,
  onBack,
  onProduct,
  onAllergens,
}: Props) {
  const text = t.tafoApp.waiter;
  const categories = sellableCategories(menu);
  const typed = query.trim() !== "";
  const current = category ?? (wide ? (categories[0]?.id ?? null) : null);
  const entries = pickEntries(menu, current, query);
  const colour = (id: string) => {
    const index = categories.findIndex((item) => item.id === id);
    return { "--cc": categories[index] ? categoryColor(categories[index], index) : undefined } as CSSProperties;
  };
  const search = (
    <input
      type="search"
      value={query}
      placeholder={text.search}
      aria-label={text.search}
      onChange={(event) => {
        onQuery(event.target.value);
      }}
    />
  );
  const allergens = (
    <button type="button" className="btn ob-algb" onClick={onAllergens}>
      <AppIcon name="allergens" />
      {text.allergens}
    </button>
  );
  const categoryOf = (product: Product) =>
    categories.find((item) => productsIn(menu, item.id).some((other) => other.id === product.id))?.id ?? "";

  /** After a tap on a category the screen glides down to its products, the categories stay above them. */
  const gridRef = useRef<HTMLDivElement>(null);
  const showProducts = () => {
    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    requestAnimationFrame(() => {
      if (gridRef.current) bringIntoView(gridRef.current, { block: "start", smooth: !still });
    });
  };
  const name = (entry: PickEntry) =>
    entry.variant === undefined ? entry.product.name : `${entry.product.name} ${entry.variant}`;

  if (wide) {
    return (
      <>
        <div className="ob-top">
          {search}
          {allergens}
        </div>
        <div className="ob-cats wrap" role="toolbar" aria-label={text.categories}>
          {categories.map((item) => (
            <button
              key={item.id}
              type="button"
              className="ob-cat"
              aria-pressed={!typed && item.id === current}
              style={colour(item.id)}
              onClick={() => {
                onCategory(item.id);
              }}
            >
              {item.name}
            </button>
          ))}
        </div>
        <div className="ob-grid">
          {entries.map((entry) => {
            const out = soldOut.has(entry.product.id);
            return (
              <button
                key={`${entry.product.id}:${entry.variant ?? ""}`}
                type="button"
                className="ob-ptile"
                disabled={out}
                aria-label={name(entry)}
                onClick={() => {
                  onProduct(entry);
                }}
              >
                <b>
                  {entry.product.name}
                  {entry.variant !== undefined && <small> {entry.variant}</small>}
                </b>
                <span className="num">{out ? text.soldOut : priceLabel(promotions, channel, entry)}</span>
              </button>
            );
          })}
          {entries.length === 0 && <p className="proto">{text.nothingFound}</p>}
        </div>
      </>
    );
  }

  // a phone: as the till on the owner's phone (Furkan, 18/10/2026): the categories as tiles on top, the
  // products below as tiles without a price, and a tap on a category glides down to its products
  const shown = typed ? entries : pickEntries(menu, current ?? categories[0]?.id ?? null, "");
  const open = typed ? null : (current ?? categories[0]?.id ?? null);
  return (
    <>
      <div className="ob-top">
        {onBack && (
          <button type="button" className="linkbtn" onClick={onBack}>
            {text.back}
          </button>
        )}
        {allergens}
      </div>
      {search}
      <section className="menu ob-menu" aria-label={text.categories}>
        {!typed && (
          <div className="cats" role="toolbar" aria-label={text.categories}>
            {categories.map((item) => (
              <button
                key={item.id}
                type="button"
                aria-pressed={item.id === open}
                style={colour(item.id)}
                onClick={() => {
                  onCategory(item.id);
                  showProducts();
                }}
              >
                {item.name}
              </button>
            ))}
          </div>
        )}
        <div className="grid" ref={gridRef}>
          {shown.map((entry) => {
            const out = soldOut.has(entry.product.id);
            return (
              <button
                key={`${entry.product.id}:${entry.variant ?? ""}`}
                type="button"
                className={`tile${out ? " soldout" : ""}`}
                style={colour(categoryOf(entry.product))}
                disabled={out}
                aria-label={name(entry)}
                onClick={() => {
                  onProduct(entry);
                }}
              >
                <span className="tn">
                  {entry.product.name}
                  {entry.variant !== undefined && <small className="sz"> {entry.variant}</small>}
                </span>
                {prices && !out && (
                  <span className="tp">
                    <b className="num">{priceLabel(promotions, channel, entry)}</b>
                  </span>
                )}
                {out && <span className="out">{text.soldOut}</span>}
              </button>
            );
          })}
          {shown.length === 0 && <p className="proto">{text.nothingFound}</p>}
        </div>
      </section>
    </>
  );
}
